<pdq-collapsed class="rule-group-card" [initialCollapsed]="false" [formGroup]="formGroup">
  <pdq-collapsed-header arrowStyle="chevron" [padding]="'var(--padding-24)'">
    <div class="rule-group-header">
      <div class="rule-group-header__left">
        <span class="rule-group-header__title">Product Rule Group {{ index + 1 }}</span>
        <span class="rule-group-header__summary">{{ summary }}</span>
      </div>
      <div class="rule-group-header__actions">
        <button
          type="button"
          class="rule-group-header__action-btn"
          matTooltip="Copy rule group"
          matTooltipClass="pdq-mat-tooltip"
          aria-label="Copy rule group"
          (click)="duplicate.emit(); $event.stopPropagation()">
          <img class="rule-group-header__action-icon" src="assets/icons/copy-to-clipboard.svg" alt="" />
        </button>
        @if (canRemove) {
          <pdq-button
            icon="trash"
            size="small"
            variant="ghost"
            matTooltip="Delete rule group"
            matTooltipClass="pdq-mat-tooltip"
            (click)="remove.emit(); $event.stopPropagation()">
          </pdq-button>
        }
      </div>
    </div>
  </pdq-collapsed-header>
  <pdq-collapsed-content>
    <pdq-divider class="rule-group-divider"></pdq-divider>
    <div class="rule-group-content">
      <!-- Products In Cart -->
      <div class="rule-section" formGroupName="productsInCart">
        <span class="rule-section__label">If Products In Cart Are:</span>
        <pdq-segmented-control
          [options]="productsInCartModeOptions"
          [value]="productsInCartMode"
          (valueChange)="setProductsInCartMode($event)">
        </pdq-segmented-control>
        @if (productsInCartMode === 'smart') {
          <pdq-rules-dynamic-form
            [formGroup]="formGroup.get('productsInCart')"
            formArrayFieldName="rules"
            [rules]="productsInCartRules"
            [entities]="productsInCartEntities"
            [shopCurrencySymbol]="shopCurrencySymbol"
            [versionWithOrOperator]="true">
          </pdq-rules-dynamic-form>
        }
      </div>

      <pdq-divider></pdq-divider>

      <!-- Upsell Products -->
      <div class="rule-section" formGroupName="upsellRecommendedProducts">
        <div class="rule-section__header">
          <span class="rule-section__label">Upsell Products Are</span>
          @if (upsellProductsMode === 'smart') {
            <button
              type="button"
              class="products-block__action products-block__action--view"
              [disabled]="loading"
              matTooltip="Click this to preview the products that match your current smart rules."
              matTooltipClass="pdq-mat-tooltip"
              (click)="onViewMatchingProducts()">
              {{ loading ? 'Loading...' : 'View products' }}
            </button>
          }
        </div>
        <pdq-segmented-control
          [options]="upsellModeOptions"
          [value]="upsellProductsMode"
          (valueChange)="setUpsellProductsMode($event)">
        </pdq-segmented-control>
        <span class="rule-section__helper-text">{{ getUpsellProductsHelperText(upsellProductsMode) }}</span>

        @if (upsellProductsMode === 'shopifyRecommendations') {
          <pdq-banner
            variant="regular"
            [closable]="false"
            bannerTitle="Shopify Search & Discovery"
            content="Products will be automatically recommended based on Shopify's Search & Discovery app. The recommendations are based on purchase history, related products, and customer behavior.">
          </pdq-banner>
        }

        @if (upsellProductsMode === 'smart') {
          <pdq-rules-dynamic-form
            [formGroup]="formGroup.get('upsellRecommendedProducts')"
            formArrayFieldName="rules"
            [rules]="upsellProductsRules"
            [entities]="upsellProductsEntities"
            [shopCurrencySymbol]="shopCurrencySymbol"
            [versionWithOrOperator]="true">
          </pdq-rules-dynamic-form>
          <!-- Extension parity: the whole matched block appears only once there are matches. -->
          @if (products.length > 0) {
            <div class="products-block">
              <div class="products-block__header">
                <span class="products-block__label">Matching Products ({{ products.length }})</span>
              </div>
              <pdq-product-list
                [products]="productsForDisplay"
                [showSearch]="true"
                [showRemove]="false"
                [showEdit]="false"
                [showExternalLink]="true"
                [currencySymbol]="shopCurrencySymbol"
                searchPlaceholder="Search..."
                emptyMessage="No matching products"
                (externalLink)="onExternalLink($event)">
              </pdq-product-list>
            </div>
          }
        }

        @if (upsellProductsMode === 'manual') {
          <div class="products-block">
            <div class="products-block__header">
              <span class="products-block__label">Product List ({{ products.length }})</span>
              <button
                type="button"
                class="products-block__action products-block__action--add"
                (click)="onAddProducts()">
                +Add Products
              </button>
            </div>
            <pdq-product-list
              [products]="productsForDisplay"
              [showSearch]="true"
              [showRemove]="true"
              [showEdit]="true"
              [showExternalLink]="true"
              [showDragHandle]="formGroup.get('prioritizeRecommendedItemsBy')?.value === 'manual'"
              [currencySymbol]="shopCurrencySymbol"
              searchPlaceholder="Search..."
              emptyMessage="No products added"
              (remove)="onRemoveProduct($event.id)"
              (edit)="onEditProduct($event)"
              (externalLink)="onExternalLink($event)"
              (reorder)="onReorderProducts($event)">
            </pdq-product-list>
          </div>
        }
      </div>

      <!-- Prioritize -->
      <div class="radio">
        <div class="radio__title">Prioritize Upsell Items By</div>
        <pdq-radio-group formControlName="prioritizeRecommendedItemsBy">
          @for (option of prioritizeOptionsForMode; track option.value) {
            <pdq-radio-button
              [value]="option.value"
              [checked]="formGroup.get('prioritizeRecommendedItemsBy')?.value === option.value"
              [text]="option.label"
              [subText]="option.description">
            </pdq-radio-button>
          }
        </pdq-radio-group>
        @if (formGroup.get('prioritizeRecommendedItemsBy')?.value === 'selling-plan') {
          <div class="selling-plan-config">
            <pdq-input
              formControlName="sellingPlanId"
              label="Selling Plan ID"
              placeholder="Enter selling plan ID"
              [invalid]="!!formGroup.errors?.sellingPlanIdRequired && !!formGroup.get('sellingPlanId')?.touched"
              [invalidMsg]="'Selling Plan ID is required'"></pdq-input>
            <div class="selling-plan-description">
              <label class="selling-plan-description__label">Selling Plan Description</label>
              <rich-textarea
                [initialContent]="formGroup.get('sellingPlanDescription')?.value"
                placeholder="Enter selling plan description..."
                [invalid]="!!formGroup.errors?.missingPricePlaceholder"
                [errorMsg]="'Description must include {price}'"
                (contentChange)="formGroup.get('sellingPlanDescription')?.setValue($event)">
              </rich-textarea>
              <span class="selling-plan-description__helper"
                >Use {{ '{' }}price{{ '}' }} to dynamically display the product price</span
              >
            </div>
          </div>
        }
      </div>

      <!-- Discount (Manual / Smart only) -->
      @if (canApplyDiscount) {
        <div class="discount-section" formGroupName="discountConfiguration">
          <pdq-checkbox
            formControlName="enabled"
            label="Apply a discount to the recommended products"
            secondaryLabel="Enabling this option will override Shopify's product discounts"
            labelSize="small">
          </pdq-checkbox>
          @if (isDiscountEnabled) {
            <div class="discount-row">
              <div class="discount-row__field discount-row__type">
                <label class="discount-row__label">Type</label>
                <pdq-dropdown
                  formControlName="discountType"
                  [options]="discountTypeOptions"
                  fullWidth="true"></pdq-dropdown>
              </div>
              <div class="discount-row__field discount-row__value">
                <label class="discount-row__label">Value</label>
                <pdq-input-arrows
                  formControlName="value"
                  [min]="1"
                  [max]="isPercentageDiscount ? 100 : undefined"
                  [precision]="2"
                  [symbol]="isPercentageDiscount ? '%' : shopCurrencySymbol"
                  widthSize="fullwidth"></pdq-input-arrows>
              </div>
            </div>

            <div class="discount-row">
              <div class="discount-row__field discount-row__label-text">
                <label class="discount-row__label">
                  Discount Badge
                  <pdq-icon
                    icon="more_information"
                    size="x-large"
                    color="var(--on-surface-light)"
                    matTooltip="Shown below the product name in the Smart Cart and in Shopify checkout. Both surfaces show the same badge."
                    matTooltipClass="pdq-mat-tooltip"></pdq-icon>
                </label>
                <pdq-input formControlName="labelText" [placeholder]="discountLabelPlaceholder"></pdq-input>
              </div>
              <div class="discount-row__field discount-row__label-color">
                <label class="discount-row__label">Label color</label>
                <pdq-input-color formControlName="labelColor"></pdq-input-color>
              </div>
            </div>
          }
        </div>
      }

      <!-- Restrictions -->
      <div class="restrictions-section">
        <div class="radio__title">Product Restrictions</div>
        <div class="restrictions-card">
          <pdq-checkbox
            formControlName="inStockProductsOnly"
            label="In-stock products only"
            secondaryLabel="Only show upsell products that have available inventory."
            labelSize="small">
          </pdq-checkbox>
          <pdq-checkbox
            formControlName="matchFulfillmentCenterToCartItems"
            label="Match fulfillment center"
            secondaryLabel="Only show products that can be fulfilled from the same location as the cart items."
            labelSize="small">
          </pdq-checkbox>
          <pdq-checkbox
            formControlName="includeDigitalItems"
            label="Include digital items"
            secondaryLabel="Allow digital products (non-shippable items) to appear as upsells."
            labelSize="small">
          </pdq-checkbox>
          <pdq-checkbox
            formControlName="matchProductVariants"
            label="Match Product Variants"
            secondaryLabel="Automatically pre-select the variant that best matches items in the customer's cart."
            labelSize="small">
          </pdq-checkbox>
          <div class="restriction-item">
            <pdq-checkbox
              [checked]="hideProductsValue !== 'none'"
              (onChange)="toggleHideProducts($event)"
              label="Hide products already in cart"
              secondaryLabel="Don't show upsell products that the customer has already added to their cart."
              labelSize="small">
            </pdq-checkbox>
            @if (hideProductsValue !== 'none') {
              <div class="restriction-nested">
                <div class="radio__title">Detection Level</div>
                <pdq-radio-group
                  [ngModel]="hideProductsValue"
                  (ngModelChange)="setHideProductsValue($event)"
                  [ngModelOptions]="{ standalone: true }">
                  @for (option of hideProductsOptions; track option.value) {
                    <pdq-radio-button
                      [value]="option.value"
                      [checked]="hideProductsValue === option.value"
                      [text]="option.label"
                      [subText]="option.description">
                    </pdq-radio-button>
                  }
                </pdq-radio-group>
              </div>
            }
          </div>
        </div>
      </div>

      <pdq-divider></pdq-divider>

      <!-- Per-group settings -->
      <div class="settings-section">
        <div class="radio__title">Settings</div>
        <div class="settings-row">
          <div class="settings-field">
            <label class="settings-field__label">Max number of items to display</label>
            <pdq-input formControlName="maxItemsToDisplay" type="number"></pdq-input>
          </div>
          <div class="settings-field">
            <label class="settings-field__label">If no items available to display</label>
            <pdq-dropdown
              formControlName="NoItemsToDisplay"
              [options]="ifNoItemsOptions"
              optionLabel="label"
              optionValue="value"></pdq-dropdown>
          </div>
        </div>
      </div>
    </div>
  </pdq-collapsed-content>
</pdq-collapsed>
